import type { ReactNode, KeyboardEvent } from 'react'
import { cn } from '@/lib/utils'
import type { Caret } from '@/lib/formulaTokens'

interface Props {
  tokens: string[]
  caret: Caret
  onCaretClick: (index: number) => void
  /** Host owns the entire interactive chip — its label/color, click-to-select,
   *  and (for kinds that support it) the in-place replace editor — since that
   *  differs per builder (field labels vs cell labels vs placeholder names). */
  renderToken: (token: string, index: number) => ReactNode
  emptyHint: ReactNode
  hasError?: boolean
}

/**
 * Shared caret display for the formula chip builders. A thin clickable gap
 * sits before every token (and once more after the last one) so the caret
 * can be placed anywhere; `renderToken` supplies each chip itself.
 *
 * Keyboard: ArrowLeft/ArrowRight step the caret one token, Home/End jump to
 * the ends — covers reaching any position without a mouse. Activating a
 * specific token (Enter/Space) is left to the host's own token buttons,
 * which receive normal DOM focus.
 */
export function FormulaTokenStrip({ tokens, caret, onCaretClick, renderToken, emptyHint, hasError }: Props) {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (tokens.length === 0) return
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      onCaretClick(Math.max(0, (caret ?? tokens.length) - 1))
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      onCaretClick(Math.min(tokens.length, (caret ?? tokens.length) + 1))
    } else if (e.key === 'Home') {
      e.preventDefault()
      onCaretClick(0)
    } else if (e.key === 'End') {
      e.preventDefault()
      onCaretClick(tokens.length)
    }
  }

  const gap = (index: number) => (
    <button
      key={`gap-${index}`}
      type='button'
      aria-label='Place cursor here'
      onClick={() => onCaretClick(index)}
      className={cn(
        'relative h-5 w-1.5 shrink-0 self-center rounded-sm',
        'hover:bg-primary/20 focus-visible:bg-primary/20 focus-visible:outline-none',
      )}
    >
      {caret === index && (
        <span className='absolute inset-y-0 left-1/2 w-px -translate-x-1/2 animate-pulse bg-primary' />
      )}
    </button>
  )

  return (
    <div
      role='group'
      aria-label='Formula'
      tabIndex={0}
      onKeyDown={onKeyDown}
      className={cn(
        'min-h-10 rounded-md border bg-muted/40 px-1 py-2 flex flex-wrap items-center gap-0.5',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        hasError && 'border-destructive/50',
      )}
    >
      {tokens.length === 0 && <span className='px-1.5 text-xs text-muted-foreground'>{emptyHint}</span>}
      {gap(0)}
      {tokens.map((tok, i) => (
        <span key={i} className='flex items-center'>
          {renderToken(tok, i)}
          {gap(i + 1)}
        </span>
      ))}
    </div>
  )
}
